import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Users, TrendingUp, Eye, Clock, ArrowUpRight } from "lucide-react";

const analyticsData = {
  overview: [
    { label: "Total Page Views", value: "847,293", change: "+12.5%", icon: Eye },
    { label: "Unique Visitors", value: "124,847", change: "+8.3%", icon: Users },
    { label: "Avg. Session Duration", value: "4m 32s", change: "+15.2%", icon: Clock },
    { label: "Revenue", value: "$24,580", change: "+18.7%", icon: TrendingUp },
  ],
};

export default function Analytics() {
  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl font-semibold tracking-tight">Analytics</h1>
        <p className="text-muted-foreground mt-1">
          Track performance, engagement, and revenue metrics.
        </p>
      </motion.div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {analyticsData.overview.map((metric, index) => (
          <motion.div
            key={metric.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <metric.icon className="w-5 h-5 text-muted-foreground" />
                <span className="text-xs text-green-500 flex items-center gap-1">
                  {metric.change}
                  <ArrowUpRight className="w-3 h-3" />
                </span>
              </div>
              <div className="mt-4">
                <p className="text-2xl font-semibold">{metric.value}</p>
                <p className="text-sm text-muted-foreground">{metric.label}</p>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Traffic Overview</h3>
        <div className="h-64 flex items-center justify-center text-muted-foreground border border-dashed rounded-lg">
          Analytics charts will be displayed here
        </div>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Top Pages</h3>
          <div className="space-y-4">
            {[
              { path: "/", views: "24,847", percentage: 28 },
              { path: "/analysis/tech-outlook", views: "18,293", percentage: 21 },
              { path: "/deep-dives/ai-future", views: "12,847", percentage: 15 },
            ].map((page) => (
              <div key={page.path} className="flex items-center gap-4">
                <div className="flex-1">
                  <p className="text-sm font-medium">{page.path}</p>
                  <p className="text-xs text-muted-foreground">{page.views} views</p>
                </div>
                <div className="w-24 h-2 bg-secondary rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${page.percentage}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Traffic Sources</h3>
          <div className="space-y-4">
            {[
              { source: "Direct", visits: "42,847", percentage: 35 },
              { source: "Google Search", visits: "38,293", percentage: 31 },
              { source: "Twitter/X", visits: "24,847", percentage: 20 },
            ].map((source) => (
              <div key={source.source} className="flex items-center gap-4">
                <div className="flex-1">
                  <p className="text-sm font-medium">{source.source}</p>
                  <p className="text-xs text-muted-foreground">{source.visits} visits</p>
                </div>
                <div className="w-24 h-2 bg-secondary rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full" style={{ width: `${source.percentage}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
